/**
 * Progress-note structure detection helpers.
 *
 * Used by both `<ProgressNoteViewer>` (to decide whether to render the
 * 5-section structured layout + Stats Strip + TOC) and `Documents.tsx` (to
 * predict the same outcome so it can hide the duplicate CCDA panels below the
 * viewer).
 *
 * History: an earlier pass gated the structured render strictly on a
 * `Device/scribe-ai-v1` `Provenance.agent` entry (`isScribeBackedProvenance`).
 * That gate proved too narrow — legacy Progress Notes (created via the older
 * SOAP flow, before the scribe device existed) never carried the Device agent,
 * so the chart's middle column silently fell back to the CCDA render with the
 * legacy "Reason for Visit" section list and no Stats Strip / TOC. Switching
 * to a permissive predicate (any locked Progress Note) gives all Progress
 * Notes the new chrome; the Stats Strip degrades to zeros and the locked
 * sections hydrate empty placeholders when the underlying QR/Claim data isn't
 * there.
 */
import type { DocumentReference, Provenance } from '@medplum/fhirtypes'
import { SCRIBE_DRAFT_PAYLOAD_EXTENSION_URL } from '../ProgressNoteEdit/scribeDraftPayload'
import { SCRIBE_TAG_SYSTEM, UNREVIEWED_AI_DRAFT_TAG_CODE } from '@/model/middleware-types'

/** beHuman tag system + code applied to externally-imported resources. */
const BEHUMAN_TAGS_SYSTEM = 'https://behuman.health/tags'
const IMPORTED_TAG_CODE = 'imported'

/**
 * Render mode for a `DocumentReference` open in the chart viewer. The viewer
 * (`<ProgressNoteViewer>`) and `Documents.tsx` both derive their layout from a
 * single call to {@link resolveNoteRenderMode} so the structured chrome and the
 * CCDA-table fallback can never disagree (the root cause of blank legacy /
 * imported progress notes).
 *
 *  - `ccda-document`      Not a Progress Note (CCD / H&P / discharge summary /
 *                         raw upload). Unchanged legacy behavior — CCDA tables
 *                         render, no structured chrome.
 *  - `external-document`  Progress Note that came from outside beHuman (Health
 *                         Gorilla import / external author). Distinct read-only
 *                         "External Record" banner + CCDA tables. NO structured
 *                         sections, NO Edit / Lock / TEFCA.
 *  - `structured-editable`  Our note, `docStatus === 'preliminary'`, with
 *                         structured data. Structured chrome + Edit / Lock.
 *  - `structured-readonly`  Our note, `docStatus === 'final'`, with structured
 *                         data. Locked structured chrome (banner + sections).
 *  - `document-fallback`  Our Progress Note with NO structured data to fill the
 *                         layout. Show the locked banner + CCDA tables instead
 *                         of empty structured sections (never-blank guarantee).
 */
export type NoteRenderMode =
  | 'ccda-document'
  | 'external-document'
  | 'structured-editable'
  | 'structured-readonly'
  | 'document-fallback'

/**
 * `true` when the DocumentReference's type text or coding marks it as a
 * Progress Note. Pure type check — says nothing about origin or structure.
 */
export function isProgressNoteType(documentReference: DocumentReference | undefined): boolean {
  if (!documentReference) return false
  const typeText = documentReference.type?.text?.toLowerCase() || ''
  const codeMatch =
    documentReference.type?.coding?.some(
      (coding) =>
        coding.code === 'ProgressNote' || coding.display?.toLowerCase().includes('progress note'),
    ) || false
  return codeMatch || typeText.includes('progress note')
}

/**
 * `true` when the note is a Care Coordinator note (category code stamped by
 * `useSubmitCareCoordinatorNote`). CC notes have no Lock concept — they are
 * never transmitted to TEFCA, so the viewer must not show lock chrome for
 * them (PD-1175). Mirrors `Documents.tsx`'s local `isCareCoordinatorNote`.
 */
export function isCareCoordinatorNote(documentReference: DocumentReference | undefined): boolean {
  return (
    documentReference?.category?.some((cat) =>
      cat.coding?.some((c) => c.code === 'care-coordinator-note'),
    ) ?? false
  )
}

/**
 * `true` when the Provenance carries a `QuestionnaireResponse` target. This is
 * the canonical "our structured note" signal: the SOAP cascade always links the
 * note's QR via `Provenance.target`. Mirrors `Documents.tsx`'s local
 * `hasQuestionnaireResponseTarget`.
 */
export function hasQuestionnaireResponseTarget(provenance: Provenance | undefined): boolean {
  return Boolean(
    provenance?.target?.some((target) => target.reference?.startsWith('QuestionnaireResponse/')),
  )
}

/**
 * `true` when the DocumentReference carries the lossless `scribe-draft-payload`
 * extension. Present on scribe notes both PRELIMINARY and FINAL — the lock
 * handler KEEPS the blob on the final note so the locked viewer can re-render
 * the full structured detail from it. A note can therefore present structured
 * data via this blob even before its QR target is wired.
 */
export function hasScribeDraftPayload(documentReference: DocumentReference | undefined): boolean {
  return Boolean(
    documentReference?.extension?.some((e) => e.url === SCRIBE_DRAFT_PAYLOAD_EXTENSION_URL),
  )
}

/**
 * `true` when the note originated OUTSIDE beHuman and should render with the
 * read-only "External Record" banner instead of our lock chrome.
 *
 * The ONE detector is the `imported` `meta.tag` (`system` ===
 * `https://behuman.health/tags`, `code` === `imported`) — the import pipeline
 * stamps it on every externally-sourced resource (HG and future sources).
 *
 * Deliberately NOT signals (PD-1224): an HG identifier / HG meta.tag — the
 * backend stamps those on OUR OWN notes when it sends them TO Health Gorilla
 * (they track the HG-side reference), so they misclassified every transmitted
 * note as external. A contained (`#`) author/custodian is likewise not proof
 * of origin.
 */
export function isExternalNote(documentReference: DocumentReference | undefined): boolean {
  if (!documentReference) return false

  return (
    documentReference.meta?.tag?.some(
      (tag) => tag.system === BEHUMAN_TAGS_SYSTEM && tag.code === IMPORTED_TAG_CODE,
    ) || false
  )
}

/**
 * PD-1330 — a preliminary note auto-created at generation completion that the
 * doctor has never reviewed: the notes tab opens it straight into the editor,
 * which shows the Review-and-Sign disclaimer and applies the stored draft. The
 * first save rewrites `meta` and clears the tag.
 */
export function isUnreviewedAiDraft(documentReference: DocumentReference | undefined): boolean {
  if (!documentReference || documentReference.docStatus !== 'preliminary') return false
  return (
    documentReference.meta?.tag?.some(
      (tag) => tag.system === SCRIBE_TAG_SYSTEM && tag.code === UNREVIEWED_AI_DRAFT_TAG_CODE,
    ) || false
  )
}

/**
 * Single source of truth for how the chart viewer renders a `DocumentReference`.
 * See {@link NoteRenderMode} for the meaning of each mode.
 *
 * Decision order (first match wins):
 *   1. Not a Progress Note → `ccda-document` (unchanged legacy CCDA render).
 *   2. External-origin Progress Note → `external-document` (External Record
 *      banner + CCDA tables; never our lock chrome).
 *   3. Our Progress Note WITH structured data (QR target on Provenance, or a
 *      scribe-draft-payload blob) → `structured-editable` when
 *      `docStatus === 'preliminary'`, else `structured-readonly`.
 *   4. Our Progress Note with NO structured data → `document-fallback`
 *      (locked banner + CCDA tables, the never-blank guarantee).
 */
export function resolveNoteRenderMode(
  documentReference: DocumentReference | undefined,
  provenance?: Provenance,
): NoteRenderMode {
  if (!isProgressNoteType(documentReference)) return 'ccda-document'

  // External imports take precedence over docStatus / structure: an imported
  // CCDA can be `final` and even carry a QR-shaped target, but it is never OUR
  // note and must show the External Record banner, not Edit / Lock.
  if (isExternalNote(documentReference)) return 'external-document'

  const hasStructuredData =
    hasQuestionnaireResponseTarget(provenance) || hasScribeDraftPayload(documentReference)

  if (!hasStructuredData) return 'document-fallback'

  return documentReference?.docStatus === 'preliminary'
    ? 'structured-editable'
    : 'structured-readonly'
}

/**
 * `true` when the Provenance carries the scribe Device agent
 * (`Device/scribe-ai-v1`) — the canonical "scribe-authored" signal, gating the
 * AI pill (the transcript accordion now gates on `hasScribeDraftPayload`).
 */
export function isScribeBackedProvenance(provenance: Provenance | undefined): boolean {
  return Boolean(
    provenance?.agent?.some(
      (a) => a.who?.reference === 'Device/scribe-ai-v1' || a.who?.display === 'scribe-ai-v1',
    ),
  )
}
